// Application runtime capability contract — the closed authority for conditional
// application WebMCP.
//
// Two types, never merged:
//   * ApplicationBlueprintRuntimeCapabilities is Library release content. It is
//     the static declaration an Application Blueprint carries, so it can never
//     carry request-scoped evidence.
//   * ApplicationRuntimeCapabilityProfile is the compiled result bound to one
//     admitted creator request, so it always carries requirement evidence refs.
//
// The deterministic Library compiler, the WebMCP runtime, the React renderer,
// and Studio persistence all consume these functions instead of re-deriving the
// rules. Every rejection is fail-closed and uses one opaque code.

/** Closed application WebMCP mode enum. */
export const APPLICATION_WEBMCP_MODES = [
  "disabled",
  "read-only",
  "action-enabled",
] as const;

export type ApplicationWebMcpMode = (typeof APPLICATION_WEBMCP_MODES)[number];

/** The only two admitted tool-reference shapes. */
export type ApplicationWebMcpToolRef =
  | `datasource:${string}`
  | `flow:${string}`;

export type ApplicationWebMcpToolKind = "datasource" | "flow";

export interface ApplicationBlueprintRuntimeCapabilities {
  webMcp: {
    mode: ApplicationWebMcpMode;
    toolRefs: string[];
    approvalPolicyRef: string | null;
  };
}

export interface ApplicationRuntimeCapabilityProfile {
  schemaVersion: "cogo.application-runtime-capabilities.v1";
  webMcp: {
    mode: ApplicationWebMcpMode;
    toolRefs: string[];
    approvalPolicyRef: string | null;
    requirementEvidenceRefs: string[];
  };
}

export interface ApplicationWebMcpToolManifestEntry {
  sourceRef: ApplicationWebMcpToolRef;
  resourceId: string;
  kind: ApplicationWebMcpToolKind;
  name: string;
  description: string;
  inputSchema: {
    type: "object";
    properties: Record<string, unknown>;
    required?: string[];
    additionalProperties: boolean;
  };
  metadata: {
    readOnly: boolean;
    confirmation: "never" | "when-mutating" | "always";
    executionAuthority: "runtime";
    retryPolicy: "never" | "safe";
    tags: string[];
  };
}

export interface ApplicationWebMcpManifest {
  schemaVersion: "cogo.application-webmcp-manifest.v1";
  approvalPolicyRef: string | null;
  tools: ApplicationWebMcpToolManifestEntry[];
}

export const APPLICATION_WEBMCP_CAPABILITY_UNAVAILABLE =
  "application-webmcp-capability-unavailable" as const;

export type ApplicationRuntimeCapabilityValidation =
  | { ok: true }
  | { ok: false; code: typeof APPLICATION_WEBMCP_CAPABILITY_UNAVAILABLE };

export const APPLICATION_RUNTIME_CAPABILITIES_SCHEMA_VERSION =
  "cogo.application-runtime-capabilities.v1" as const;

export const APPLICATION_WEBMCP_MANIFEST_SCHEMA_VERSION =
  "cogo.application-webmcp-manifest.v1" as const;

/** Exact identifier grammar shared with the deterministic exact namespace. */
const EXACT_LOCAL_ID = /^[a-z][a-z0-9-]*$/;

/** Versioned approval policy reference grammar. */
const APPROVAL_POLICY_REF = /^cogo:approval-policy\/[a-z0-9][a-z0-9-]*@[0-9]+\.[0-9]+\.[0-9]+$/;

/** Maximum length of one opaque requirement evidence reference. */
const MAX_EVIDENCE_REF_LENGTH = 512;

const OK: ApplicationRuntimeCapabilityValidation = { ok: true };
const UNAVAILABLE: ApplicationRuntimeCapabilityValidation = {
  ok: false,
  code: APPLICATION_WEBMCP_CAPABILITY_UNAVAILABLE,
};

export function isApplicationWebMcpMode(
  value: unknown,
): value is ApplicationWebMcpMode {
  return (
    typeof value === "string" &&
    (APPLICATION_WEBMCP_MODES as readonly string[]).includes(value)
  );
}

export function isApplicationApprovalPolicyRef(value: unknown): value is string {
  return typeof value === "string" && APPROVAL_POLICY_REF.test(value);
}

/**
 * Parse one admitted tool reference. Exactly `datasource:<id>` and `flow:<id>`
 * are admitted; every other shape returns null.
 */
export function parseApplicationWebMcpToolRef(
  value: string,
): { kind: ApplicationWebMcpToolKind; localId: string } | null {
  if (typeof value !== "string") return null;
  const separator = value.indexOf(":");
  if (separator < 0) return null;
  const prefix = value.slice(0, separator);
  const localId = value.slice(separator + 1);
  if (prefix !== "datasource" && prefix !== "flow") return null;
  if (!EXACT_LOCAL_ID.test(localId)) return null;
  return { kind: prefix, localId };
}

/**
 * Canonical tool-ref order: datasource refs sorted first, then flow refs sorted.
 *
 * WARNING: this function DROPS any ref that `parseApplicationWebMcpToolRef`
 * rejects — the returned array can be shorter than the input. It is an ordering
 * helper, not a validator. Call it only on refs that
 * `validateApplicationBlueprintRuntimeCapabilities` /
 * `validateApplicationRuntimeCapabilities` already admitted, so that dropping is
 * unreachable; never use a length-preserving assumption on its output.
 */
export function canonicalApplicationWebMcpToolRefs(
  toolRefs: readonly string[],
): string[] {
  const datasources: string[] = [];
  const flows: string[] = [];
  for (const ref of toolRefs) {
    const parsed = parseApplicationWebMcpToolRef(ref);
    if (parsed?.kind === "datasource") datasources.push(ref);
    else if (parsed?.kind === "flow") flows.push(ref);
  }
  return [...datasources.sort(compareText), ...flows.sort(compareText)];
}

/** Validate the static Blueprint declaration (no request-scoped evidence). */
export function validateApplicationBlueprintRuntimeCapabilities(
  declaration: unknown,
): ApplicationRuntimeCapabilityValidation {
  if (!hasExactKeys(declaration, ["webMcp"])) return UNAVAILABLE;
  const webMcp = (declaration as { webMcp: unknown }).webMcp;
  if (!hasExactKeys(webMcp, ["mode", "toolRefs", "approvalPolicyRef"])) {
    return UNAVAILABLE;
  }
  return validateWebMcpCore(webMcp as Record<string, unknown>).ok
    ? OK
    : UNAVAILABLE;
}

/**
 * Validate one compiled profile and, when the mode enables tools, the manifest
 * that must accompany it. `disabled` rejects any manifest; every enabled mode
 * requires one that matches the admitted refs exactly.
 */
export function validateApplicationRuntimeCapabilities(
  profile: unknown,
  manifest?: unknown,
): ApplicationRuntimeCapabilityValidation {
  if (!hasExactKeys(profile, ["schemaVersion", "webMcp"])) return UNAVAILABLE;
  const candidate = profile as Record<string, unknown>;
  if (candidate.schemaVersion !== APPLICATION_RUNTIME_CAPABILITIES_SCHEMA_VERSION) {
    return UNAVAILABLE;
  }
  if (
    !hasExactKeys(candidate.webMcp, [
      "mode",
      "toolRefs",
      "approvalPolicyRef",
      "requirementEvidenceRefs",
    ])
  ) {
    return UNAVAILABLE;
  }
  const webMcp = candidate.webMcp as Record<string, unknown>;
  const core = validateWebMcpCore(webMcp);
  if (!core.ok) return UNAVAILABLE;

  const evidenceRefs = webMcp.requirementEvidenceRefs;
  if (!isOpaqueRefList(evidenceRefs)) return UNAVAILABLE;
  const enabled = core.mode !== "disabled";
  if (enabled !== evidenceRefs.length > 0) return UNAVAILABLE;

  if (!enabled) {
    return manifest === undefined ? OK : UNAVAILABLE;
  }
  return validateManifest(manifest, core.toolRefs, core.approvalPolicyRef)
    ? OK
    : UNAVAILABLE;
}

function validateWebMcpCore(
  webMcp: Record<string, unknown>,
):
  | { ok: false }
  | {
      ok: true;
      mode: ApplicationWebMcpMode;
      toolRefs: string[];
      approvalPolicyRef: string | null;
    } {
  const mode = webMcp.mode;
  if (!isApplicationWebMcpMode(mode)) return { ok: false };

  const toolRefs = webMcp.toolRefs;
  if (!Array.isArray(toolRefs)) return { ok: false };
  const kinds: ApplicationWebMcpToolKind[] = [];
  for (const ref of toolRefs) {
    if (typeof ref !== "string") return { ok: false };
    const parsed = parseApplicationWebMcpToolRef(ref);
    if (!parsed) return { ok: false };
    kinds.push(parsed.kind);
  }
  if (new Set(toolRefs as string[]).size !== toolRefs.length) {
    return { ok: false };
  }

  const approvalPolicyRef = webMcp.approvalPolicyRef;
  if (approvalPolicyRef !== null && !isApplicationApprovalPolicyRef(approvalPolicyRef)) {
    return { ok: false };
  }

  if (mode === "disabled") {
    if (toolRefs.length > 0 || approvalPolicyRef !== null) return { ok: false };
  } else if (mode === "read-only") {
    if (toolRefs.length === 0 || approvalPolicyRef !== null) return { ok: false };
    if (kinds.some((kind) => kind !== "datasource")) return { ok: false };
  } else {
    if (!kinds.includes("flow")) return { ok: false };
    if (!isApplicationApprovalPolicyRef(approvalPolicyRef)) return { ok: false };
  }

  return {
    ok: true,
    mode,
    toolRefs: toolRefs as string[],
    approvalPolicyRef: approvalPolicyRef as string | null,
  };
}

function validateManifest(
  manifest: unknown,
  toolRefs: readonly string[],
  approvalPolicyRef: string | null,
): boolean {
  if (!hasExactKeys(manifest, ["schemaVersion", "approvalPolicyRef", "tools"])) {
    return false;
  }
  const candidate = manifest as Record<string, unknown>;
  if (candidate.schemaVersion !== APPLICATION_WEBMCP_MANIFEST_SCHEMA_VERSION) {
    return false;
  }
  if (candidate.approvalPolicyRef !== approvalPolicyRef) return false;

  const tools = candidate.tools;
  if (!Array.isArray(tools) || tools.length !== toolRefs.length) return false;

  const canonical = canonicalApplicationWebMcpToolRefs(toolRefs);
  const names = new Set<string>();
  const resourceIds = new Set<string>();
  for (let index = 0; index < tools.length; index += 1) {
    const tool = tools[index];
    if (
      !hasExactKeys(tool, [
        "sourceRef",
        "resourceId",
        "kind",
        "name",
        "description",
        "inputSchema",
        "metadata",
      ])
    ) {
      return false;
    }
    const entry = tool as Record<string, unknown>;
    if (entry.sourceRef !== canonical[index]) return false;
    const parsed = parseApplicationWebMcpToolRef(entry.sourceRef as string);
    if (!parsed || entry.kind !== parsed.kind) return false;
    if (!isNonBlankString(entry.resourceId)) return false;
    if (!isNonBlankString(entry.name)) return false;
    if (!isNonBlankString(entry.description)) return false;
    if (names.has(entry.name)) return false;
    if (resourceIds.has(entry.resourceId)) return false;
    names.add(entry.name);
    resourceIds.add(entry.resourceId);
    if (!isClosedInputSchema(entry.inputSchema)) return false;
    if (!isExactToolMetadata(entry.metadata, parsed.kind)) return false;
  }
  return true;
}

function isClosedInputSchema(value: unknown): boolean {
  if (!isPlainRecord(value)) return false;
  const keys = Object.keys(value);
  const allowed = ["type", "properties", "required", "additionalProperties"];
  if (keys.some((key) => !allowed.includes(key))) return false;
  if (!keys.includes("type") || !keys.includes("properties")) return false;
  if (!keys.includes("additionalProperties")) return false;
  if (value.type !== "object") return false;
  if (!isPlainRecord(value.properties)) return false;
  if (value.additionalProperties !== false) return false;
  if (
    "required" in value &&
    (!Array.isArray(value.required) ||
      value.required.some((item) => !isNonBlankString(item)))
  ) {
    return false;
  }
  return true;
}

function isExactToolMetadata(
  value: unknown,
  kind: ApplicationWebMcpToolKind,
): boolean {
  if (
    !hasExactKeys(value, [
      "readOnly",
      "confirmation",
      "executionAuthority",
      "retryPolicy",
      "tags",
    ])
  ) {
    return false;
  }
  const metadata = value as Record<string, unknown>;
  if (metadata.executionAuthority !== "runtime") return false;
  if (!Array.isArray(metadata.tags)) return false;
  if (metadata.tags.some((tag) => !isNonBlankString(tag))) return false;

  if (kind === "datasource") {
    return (
      metadata.readOnly === true &&
      metadata.confirmation === "never" &&
      metadata.retryPolicy === "safe"
    );
  }
  return (
    metadata.readOnly === false &&
    (metadata.confirmation === "when-mutating" ||
      metadata.confirmation === "always") &&
    metadata.retryPolicy === "never"
  );
}

function isOpaqueRefList(value: unknown): value is string[] {
  if (!Array.isArray(value)) return false;
  if (
    value.some(
      (item) =>
        !isNonBlankString(item) || item.length > MAX_EVIDENCE_REF_LENGTH,
    )
  ) {
    return false;
  }
  return new Set(value as string[]).size === value.length;
}

function isNonBlankString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value)
  );
}

function hasExactKeys(value: unknown, keys: readonly string[]): boolean {
  if (!isPlainRecord(value)) return false;
  const own = Object.keys(value);
  return own.length === keys.length && keys.every((key) => own.includes(key));
}

function compareText(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}
